/**
 * @nanokart/tryon-studio-sdk
 * TryOnProvider - Context provider
 */

import React, { createContext, useEffect, useMemo, useRef } from 'react';
import { StoreApi } from 'zustand';

import { TryOnStudioConfig, ResolvedFeatures } from '../types/config';
import { TryOnStudioEvents, createBaseEvent, ReadyEvent, SessionEndEvent } from '../types/events';
import { HostAdapter, createHostAdapter } from './adapters';
import { CatalogProvider, createCatalogProvider } from './providers';
import { TryOnStore, createTryOnStore } from './state/store';
import { resolveFeatures, resolveUIVisibility } from '../config/modes';

// ============================================================================
// Context Types
// ============================================================================

export interface TryOnConfigContextValue {
  config: TryOnStudioConfig;
  features: ResolvedFeatures;
  uiVisibility: ReturnType<typeof resolveUIVisibility>;
  hostAdapter: HostAdapter;
  catalogProvider: CatalogProvider;
  events: TryOnStudioEvents;
}

// ============================================================================
// Contexts
// ============================================================================

export const TryOnContext = createContext<StoreApi<TryOnStore> | null>(null);
export const TryOnConfigContext = createContext<TryOnConfigContextValue | null>(null);

// ============================================================================
// Provider Props
// ============================================================================

export interface TryOnProviderProps {
  config: TryOnStudioConfig;
  events?: TryOnStudioEvents;
  hostAdapter?: HostAdapter;
  catalogProvider?: CatalogProvider;
  children: React.ReactNode;
}

// ============================================================================
// Provider Component
// ============================================================================

export const TryOnProvider: React.FC<TryOnProviderProps> = ({
  config,
  events = {},
  hostAdapter: customHostAdapter,
  catalogProvider: customCatalogProvider,
  children,
}) => {
  const storeRef = useRef<StoreApi<TryOnStore> | null>(null);
  if (!storeRef.current) {
    storeRef.current = createTryOnStore(config);
  }
  const store = storeRef.current;

  const mode = config.mode ?? 'embedded-commerce';
  const features = useMemo(() => resolveFeatures(mode, config.features), [mode, config.features]);
  const uiVisibility = useMemo(() => resolveUIVisibility(mode), [mode]);

  const hostAdapter = useMemo(() => {
    if (customHostAdapter) return customHostAdapter;
    return createHostAdapter({
      platformContext: config.platformContext ?? 'standalone',
      callbacks: {
        onAddToCart: (intent) => {
          const event = {
            ...createBaseEvent('add_to_cart'),
            payload: {
              productId: intent.productId,
              productName: intent.productName,
              variantId: intent.variantId,
              size: intent.size,
              color: intent.color,
              price: intent.price,
              currency: intent.currency,
              quantity: intent.quantity,
              tryonImageUrl: intent.metadata?.tryonImageUrl as string | undefined,
            },
          };
          events.onAddToCart?.(event as any);
        },
        onAnalyticsEvent: (analyticsEvent) => {
          events.onAnalyticsEvent?.(analyticsEvent as any);
        },
      },
    });
  }, [customHostAdapter, config.platformContext, events]);

  const catalogProvider = useMemo(() => {
    if (customCatalogProvider) return customCatalogProvider;
    return createCatalogProvider({
      items: config.items,
      catalogUrl: config.catalogUrl,
      catalogHeaders: config.catalogHeaders,
      mapCatalogItem: config.mapCatalogItem,
    });
  }, [
    customCatalogProvider,
    config.items,
    config.catalogUrl,
    config.catalogHeaders,
    config.mapCatalogItem,
  ]);

  const configContextValue = useMemo<TryOnConfigContextValue>(
    () => ({ config, features, uiVisibility, hostAdapter, catalogProvider, events }),
    [config, features, uiVisibility, hostAdapter, catalogProvider, events]
  );

  // Initialize on mount
  useEffect(() => {
    const init = async () => {
      await store.getState().initialize();

      const readyEvent: ReadyEvent = {
        ...createBaseEvent('ready'),
        eventName: 'ready',
        payload: { partnerId: config.partnerId, mode, features },
      };
      events.onReady?.(readyEvent);
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Set catalog provider
  useEffect(() => {
    if (catalogProvider) {
      store.getState().setCatalogProvider(catalogProvider);
    }
  }, [catalogProvider, store]);

  // Subscribe to session end
  useEffect(() => {
    const unsubscribe = store.subscribe((state, prevState) => {
      if (
        prevState.studioSession.status !== 'ended' &&
        state.studioSession.status === 'ended'
      ) {
        const duration = state.studioSession.startedAt
          ? Math.floor(
              (Date.now() - new Date(state.studioSession.startedAt).getTime()) / 1000
            )
          : 0;

        const event: SessionEndEvent = {
          ...createBaseEvent('session_end'),
          eventName: 'session_end',
          payload: {
            duration,
            tryonsGenerated: state.studioSession.stats.tryonsGenerated,
            momentsGenerated: state.studioSession.stats.momentsGenerated,
            itemsInCart: state.cart.hardCart.length,
            endReason: 'user_action',
          },
        };
        events.onSessionEnd?.(event);
      }
    });
    return unsubscribe;
  }, [store, events]);

  // Fire onTryOnComplete ONLY when a new generation just completed
  // (isGenerating: true → false with a new result), NOT for cache hits.
  useEffect(() => {
    const unsubscribe = store.subscribe((state, prevState) => {
      const wasGenerating = prevState.tryon.isGenerating;
      const isNowDone = !state.tryon.isGenerating;
      const hasNewResult =
        state.tryon.currentResult &&
        state.tryon.currentResult !== prevState.tryon.currentResult;

      if (wasGenerating && isNowDone && hasNewResult) {
        const current = state.tryon.currentResult!;
        const selectedItem = state.inventory.selectedItem;
        events.onTryOnComplete?.({
          ...createBaseEvent('tryon_complete'),
          eventName: 'tryon_complete',
          payload: {
            productId: current.productId,
            productName: selectedItem?.name ?? '',
            resultImageUrl: current.resultImageUrl,
            inputPhotoUrl: current.inputPhotoUrl,
            garmentImageUrl: current.garmentImageUrl,
            cacheKey: current.cacheKey,
            processingTimeMs: current.processingTimeMs,
          },
        });
      }
    });
    return unsubscribe;
  }, [store, events]);

  // Fire onMomentGenerated when a moment generation completes
  useEffect(() => {
    const unsubscribe = store.subscribe((state, prevState) => {
      const wasGenerating = prevState.moments.isGenerating;
      const isNowDone = !state.moments.isGenerating;

      if (wasGenerating && isNowDone) {
        // Find the newly generated moment result
        const momentType = wasGenerating;
        for (const [cacheKey, results] of Object.entries(state.moments.results)) {
          const prevResults = prevState.moments.results[cacheKey] ?? [];
          const newResult = results.find(
            (r) => r.momentType === momentType &&
              !prevResults.some((pr) => pr.momentType === r.momentType)
          );
          if (newResult) {
            events.onMomentGenerated?.({
              ...createBaseEvent('moment_generated'),
              eventName: 'moment_generated',
              payload: {
                momentType: newResult.momentType,
                resultImageUrl: newResult.resultImageUrl,
                tryonCacheKey: cacheKey,
                processingTimeMs: newResult.processingTimeMs,
              },
            });
            break;
          }
        }
      }
    });
    return unsubscribe;
  }, [store, events]);

  return (
    <TryOnContext.Provider value={store}>
      <TryOnConfigContext.Provider value={configContextValue}>
        {children}
      </TryOnConfigContext.Provider>
    </TryOnContext.Provider>
  );
};

// ============================================================================
// Custom Hook
// ============================================================================

export function useTryOnConfig(): TryOnConfigContextValue {
  const context = React.useContext(TryOnConfigContext);
  if (!context) {
    throw new Error('useTryOnConfig must be used within TryOnProvider');
  }
  return context;
}
